import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";

/**
 * useFileToken — mints a short-lived `file`-purpose download token and
 * auto-refreshes it before expiry. Backend TTL is 5 minutes; we refresh
 * every ~4 minutes so any link generated with this token is guaranteed
 * to be valid when tapped.
 *
 * Why: iOS Home-Screen PWAs open `target="_blank"` links in external
 * Safari, which does not share the PWA's auth cookie. Signed URLs (via
 * `?token=…` on `/api/files/{path}`) work because the token is
 * self-contained. Once minted, we embed it in every photo `<a href>` on
 * the page so a tap in Safari lands on the image without a login redirect.
 */
const REFRESH_INTERVAL_MS = 4 * 60 * 1000; // 4 min (backend TTL is 5 min)

export function useFileToken() {
  const [token, setToken] = useState("");
  const timerRef = useRef(null);

  const mint = useCallback(async () => {
    try {
      const { data } = await api.post("/export/tokens", { purpose: "file" });
      if (data?.token) setToken(data.token);
    } catch {
      // Non-fatal — clicks will still open in-app; only the "external
      // Safari" path degrades (needs login).
    }
  }, []);

  useEffect(() => {
    mint();
    timerRef.current = setInterval(mint, REFRESH_INTERVAL_MS);
    return () => clearInterval(timerRef.current);
  }, [mint]);

  return token;
}

/**
 * Build a `/photo?src=…&token=…` deep link. Callers pass the storage
 * `path` (as it lives on personal_entries.attachment_url or
 * entries.photo_path) plus optional supplier / date so the viewer's
 * title bar is populated.
 */
export function buildPhotoHref({ path, token, supplier, name }) {
  if (!path) return "#";
  const params = new URLSearchParams({ src: path });
  if (token) params.set("token", token);
  if (supplier) params.set("supplier", supplier);
  if (name) params.set("name", name);
  return `/photo?${params.toString()}`;
}
